import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { KeyRound } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { UserRole } from '@/types/enums';
import { apiClient } from '@/services/api/client';
import { authApi } from '@/services/api/authApi';
import type { TokenResponse } from '@/types/api';
import { Button, Input } from '@/components/ui';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

const changePasswordSchema = z
  .object({
    new_password: z.string().min(8, 'Минимум 8 символов'),
    confirm_password: z.string().min(1, 'Подтвердите пароль'),
  })
  .refine((data) => data.new_password === data.confirm_password, {
    message: 'Пароли не совпадают',
    path: ['confirm_password'],
  });

type ChangePasswordForm = z.infer<typeof changePasswordSchema>;

const ChangePasswordPage: React.FC = () => {
  const { loginWithTokens, setUser, user } = useAuthStore();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ChangePasswordForm>({
    resolver: zodResolver(changePasswordSchema),
  });

  const onSubmit = async (data: ChangePasswordForm) => {
    try {
      const response = await apiClient.post<TokenResponse>('/v1/auth/change-password', {
        new_password: data.new_password,
      });
      loginWithTokens(response.data);

      const me = await authApi.getMe();
      setUser(me);

      toast.success('Пароль успешно изменён');

      const defaultPath =
        me.role === UserRole.ADMIN
          ? '/admin/dashboard'
          : me.role === UserRole.CUSTOMER
            ? '/my-orders'
            : '/dashboard';
      navigate(defaultPath, { replace: true });
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Не удалось изменить пароль'));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto mb-4 shadow-card">
            <KeyRound className="w-8 h-8 text-amber-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Смена пароля</h1>
          <p className="text-sm text-gray-500 mt-1">
            {user?.must_change_password
              ? 'Для продолжения работы необходимо сменить пароль'
              : 'Введите новый пароль'}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-soft border border-gray-100 p-8">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <Input
              label="Новый пароль"
              type="password"
              placeholder="Минимум 8 символов"
              error={errors.new_password?.message}
              required
              {...register('new_password')}
            />

            <Input
              label="Подтвердите пароль"
              type="password"
              placeholder="Повторите пароль"
              error={errors.confirm_password?.message}
              required
              {...register('confirm_password')}
            />

            <Button type="submit" loading={isSubmitting} className="w-full">
              Сменить пароль
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordPage;
